import React, { useEffect } from "react";
import { message } from "antd";
import { mediamtxService } from "@/mediaservices/mediamtxService";
import { mediasoupService } from "@/mediaservices/mediasoupService";
import { setAuth, getAuth } from "@/api/clientService";

const USE_MEDIAMTX =
  String(import.meta.env.VITE_USE_MEDIAMTX).toLowerCase() === "true";

const WebSocketProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  useEffect(() => {
    let mounted = true;
    let connectionInitiated = false;
    let isReconnecting = false; // Track if we're in reconnection state
    let reconnectTimeoutId: NodeJS.Timeout | null = null; // Track timeout for resetting reconnection state

    // ---------------------- Initialize WebSocket on app start ----------------------
    const initializeWebSocket = async () => {
      if (connectionInitiated) {
        console.log("⚠️ [WebSocketProvider] Connection already initiated");
        return;
      }
      connectionInitiated = true;

      // Check if user is authenticated
      const auth = getAuth();
      if (!auth || !auth.accessToken) {
        console.log("⚠️ [WebSocketProvider] No auth token found - skipping WebSocket initialization");
        return;
      }

      console.log("🔄 [WebSocketProvider] Initializing WebSocket connection...");

      // Wait a bit for auth to be ready
      await new Promise(resolve => setTimeout(resolve, 300));

      if (!mounted) return;

      // Check if already connected (e.g., from login page)
      if (USE_MEDIAMTX && mediamtxService.isConnected()) {
        console.log("✅ [WebSocketProvider] WebSocket already connected");
        return;
      }

      try {
        if (USE_MEDIAMTX) {
          const connectionPromise = mediamtxService.initialize();
          const timeoutPromise = new Promise<never>((_, reject) => {
            setTimeout(() => reject(new Error('Connection timeout')), 15000);
          });

          await Promise.race([connectionPromise, timeoutPromise]);

          if (mediamtxService.isConnected()) {
            console.log("✅ [WebSocketProvider] WebSocket connected successfully");
            message.success({
              content: "Connected",
              duration: 2,
              key: "ws-connected",
            });
          } else {
            throw new Error('Connection verification failed');
          }
        }
      } catch (error: any) {
        console.error("❌ [WebSocketProvider] Connection failed:", error);
        
        // Show error message to user
        // message.error({
        //   content: "Failed to connect. Please try logging in again.",
        //   duration: 5,
        //   key: "ws-failed",
        // });

        // If it's an auth error, clear auth and redirect to login after delay
        if (error.message?.includes('auth') || error.message?.includes('login')) {
          setTimeout(() => {
            if (mounted) {
              setAuth(null);
              window.location.href = '/login';
            }
          }, 2000);
        }
      }
    };

    // Start initialization
    initializeWebSocket();

    // Handle WebSocket disconnection events
    const handleWebSocketDisconnect = (data: any) => {
      const reason = data?.reason || "";
      
      console.log("🔌 WebSocket disconnected:", reason);

      // For "transport close" and "transport error", let MediaMTX service handle reconnection
      // It has built-in retry logic (5 attempts with 3-second delays)
      // Show reconnecting message to inform user
      if (reason === "transport close" || reason === "transport error") {
        // Clear any existing timeout
        if (reconnectTimeoutId) {
          clearTimeout(reconnectTimeoutId);
          reconnectTimeoutId = null;
        }
        
        isReconnecting = true; // Mark that we're in reconnection state
        message.loading({
          content: "Connection lost. Reconnecting...",
          duration: 0, // Keep showing until reconnect succeeds or fails
          key: "reconnecting",
        });
        return;
      }

      // Only treat "ping timeout" as immediate session expiry since that indicates
      // prolonged connection loss where reconnection is unlikely to succeed
      if (reason === "ping timeout") {
        // Show session expired message for ping timeout
        message.error({
          content: "Your session has expired due to connection loss. Please log in again.",
          duration: 8,
          key: "session-expired",
        });

        // Clear current auth
        setAuth(null);

        // Disconnect WebSocket
        if (USE_MEDIAMTX) {
          mediamtxService.disconnect();
        } else {
          mediasoupService.disconnect();
        }

        // Redirect to login
        setTimeout(() => {
          window.location.href = "/login";
        }, 500);
      }
    };

    //Handle ONLY authentication-related WebSocket errors
    const handleWebSocketAuthError = (data: any) => {
      const errorMessage =
        data?.message || data?.data?.message || "";
      const eventType = data?.event || "";

      //Check for authentication-related errors (NOT admin_mute)
      const isAuthError =
        errorMessage.includes("Operator is already logged in") ||
        errorMessage.includes("Admin authentication required") ||
        eventType === "auth_admin" ||
        data?.type === "authentication_failed";

      if (isAuthError) {
        // Clear current auth
        setAuth(null);

        // Determine appropriate error message
        let userMessage = "Authentication error occurred. You have been logged out.";

        // If we're reconnecting, show session expired message for any auth error
        if (isReconnecting) {
          userMessage = "Your session has expired. Please log in again.";
        } else if (errorMessage.includes("Operator is already logged in")) {
          userMessage = "Another operator has logged in. You have been logged out.";
        } else if (errorMessage.includes("Admin authentication required")) {
          userMessage = "Your session has expired. Please log in again.";
        }

        // Show error message
        message.error({
          content: userMessage,
          duration: 8,
          key: "auth-error",
        });

        // Disconnect WebSocket
        if (USE_MEDIAMTX) {
          mediamtxService.disconnect();
        } else {
          mediasoupService.disconnect();
        }

        // Use window.location instead of navigate
        setTimeout(() => {
          window.location.href = "/login";
        }, 1000);
      }
    };

    // Handle general exceptions - check ONLY for auth_admin events
    const handleWebSocketException = (data: any) => {
      console.log("🚨 WebSocket exception received:", data);

      const errorData = data?.data || data;
      const eventType = data?.event;
      const errorMessage = errorData?.message || "";

      //ONLY check auth_admin events and authentication messages (NOT admin_mute)
      if (eventType === "auth_admin" || 
          errorMessage.includes('Admin authentication required') ||
          errorMessage.includes('Operator is already logged in')) {
        
        // Treat as auth error
        handleWebSocketAuthError({
          message: errorMessage,
          event: eventType,
          data: errorData
        });
      }
    };

    const onAuthLogout = () => {
      if (USE_MEDIAMTX) {
        mediamtxService.disconnect();
        window.dispatchEvent(new Event("mediamtx:disconnected"));
      } else {
        mediasoupService.disconnect();
        window.dispatchEvent(new Event("mediasoup:disconnected"));
      }
    };

    // Handle reconnection failure after all retry attempts
    const handleReconnectFailed = (data: any) => {
      console.error("🔌 All reconnection attempts failed:", data);
      
      message.error({
        content: "Connection lost. Please log in again.",
        duration: 8,
        key: "reconnect-failed",
      });

      // Clear auth
      setAuth(null);

      // Disconnect WebSocket
      if (USE_MEDIAMTX) {
        mediamtxService.disconnect();
      } else {
        mediasoupService.disconnect();
      }

      // Redirect to login
      setTimeout(() => {
        window.location.href = "/login";
      }, 1000);
    };

    // Handle successful connection/reconnection
    const handleWebSocketConnected = (data: any) => {
      console.log("✅ WebSocket connected:", data);
      
      // Dismiss reconnecting message and show success
      message.destroy("reconnecting");
      // message.success({
      //   content: "Connected successfully",
      //   duration: 2,
      //   key: "connected",
      // });
      
      // Keep reconnecting state active for 3 seconds after successful reconnection
      // to catch any auth errors that might come through after reconnection
      if (isReconnecting) {
        // Clear any existing timeout
        if (reconnectTimeoutId) {
          clearTimeout(reconnectTimeoutId);
        }
        
        reconnectTimeoutId = setTimeout(() => {
          isReconnecting = false;
          reconnectTimeoutId = null;
          console.log("🔄 Reconnection window closed");
        }, 3000); // Keep flag active for 3 seconds
      }
    };

    window.addEventListener("auth:logout", onAuthLogout);

    // Listen for auth_error, exception, disconnection, reconnection, and reconnect_failed events
    if (USE_MEDIAMTX) {
      mediamtxService.on("connected", handleWebSocketConnected);
      mediamtxService.on("auth_error", handleWebSocketAuthError);
      mediamtxService.on("exception", handleWebSocketException);
      mediamtxService.on("disconnected", handleWebSocketDisconnect);
      mediamtxService.on("reconnect_failed", handleReconnectFailed);
    } else {
      // MediaSoup only supports limited events, handle disconnection
      mediasoupService.on("disconnected", handleWebSocketDisconnect);
    }

    return () => {
      mounted = false;
      
      // Clear reconnection timeout
      if (reconnectTimeoutId) {
        clearTimeout(reconnectTimeoutId);
      }
      
      window.removeEventListener("auth:logout", onAuthLogout);

      // Cleanup error listeners
      if (USE_MEDIAMTX) {
        mediamtxService.off("connected", handleWebSocketConnected);
        mediamtxService.off("auth_error", handleWebSocketAuthError);
        mediamtxService.off("exception", handleWebSocketException);
        mediamtxService.off("disconnected", handleWebSocketDisconnect);
        mediamtxService.off("reconnect_failed", handleReconnectFailed);
      } else {
        mediasoupService.off("disconnected", handleWebSocketDisconnect);
      }
    };
  }, []);

  return <>{children}</>;
};

export default WebSocketProvider;
